import type { DashboardMapPin } from "@/components/dashboard/dashboard-map";
import { DashboardViewToggle } from "@/components/dashboard/dashboard-view-toggle";
import { buildColumnFilterBadges } from "@/components/non_registrant/dashboard-column-filter-utils";
import {
  type ColumnProperties,
  type ExportColumnMode,
  dashboardTableLayoutBaseKey,
  exportTableRowsToCsv,
  exportTableRowsToExcel,
  getExportAccessorKeysForMode,
} from "@/components/non_registrant/dashboard-table";
import {
  STR_PROPERTY_PIN_LEGEND,
  StrCatalogFrame,
  type StrCatalogSearch,
  parseStrCatalogFilters,
  serializeStrCatalogFilters,
  strPropertyMapPinColor,
  validateStrCatalogSearch,
} from "@/components/non_registrant/str-catalog";
import { StrLinkedLicenseSheet } from "@/components/non_registrant/str-linked-license-sheet";
import {
  StrPropertyDetailsSheet,
  safeHttpUrl,
  scopedStrLicenseId,
} from "@/components/non_registrant/str-property-details";
import { strStatusDisplayLabel } from "@/components/non_registrant/str-property-pin-card";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { UserType } from "@/constants";
import { regionQueryParams } from "@/hera-api/regions-api";
import {
  type StrGovernmentPin,
  type StrGovernmentProperty,
  getAllGovernmentStrProperties,
  getGovernmentStrPins,
  getGovernmentStrProperties,
  getGovernmentStrProperty,
  getGovernmentStrPropertyFilterValues,
} from "@/hera-api/str-government-api";
import { useGovernmentUserDashboardState } from "@/hooks/use-government-user-dashboard-state";
import { useRegionFilterOptions } from "@/hooks/use-region-filter";
import { useListColumnFilterSync } from "@/hooks/use-sync-global-column-filters";
import { useSyncSharedTextColumns } from "@/hooks/use-sync-shared-text-columns";
import {
  LICENSE_SHARED_TEXT_COLUMNS,
  STR_SHARED_TEXT_COLUMNS,
} from "@/lib/shared-map-list-column-filters";
import { getQueryErrorMessage } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import {
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

const PAGE_SIZES = [10, 25, 50, 100] as const;
type PageSize = (typeof PAGE_SIZES)[number];
type ColumnKey =
  | "property"
  | "status"
  | "regions"
  | "platforms"
  | "city"
  | "listings";

const COLUMN_KEYS: ColumnKey[] = [
  "property",
  "status",
  "regions",
  "platforms",
  "city",
  "listings",
];

export const GOVERNMENT_STR_COLUMN_KEYS = COLUMN_KEYS;

export function validateGovernmentStrSearch(
  search: Record<string, unknown>,
): StrCatalogSearch {
  return validateStrCatalogSearch(search, "property", COLUMN_KEYS);
}

function propertyFromPin(pin: StrGovernmentPin): StrGovernmentProperty {
  return {
    id: pin.id,
    listing_count: pin.license_count,
    platforms: pin.platforms ?? [],
    title: pin.title,
    address: pin.address ?? null,
    city: null,
    mapped: pin.mapped,
    compliance: pin.compliance,
    pin_color: pin.pin_color,
    status_label: pin.status_label,
    regions: [],
    latitude: pin.latitude,
    longitude: pin.longitude,
    property_id: null,
    licenses: [],
  };
}

export function governmentStrMapPins(
  pins: StrGovernmentPin[],
): DashboardMapPin[] {
  return pins
    .filter(
      (pin) => Number.isFinite(pin.latitude) && Number.isFinite(pin.longitude),
    )
    .map((pin) => ({
      id: pin.id,
      lat: pin.latitude,
      lng: pin.longitude,
      display_id: pin.title || "Short-term rental",
      address: pin.address?.trim() || pin.title || "Short-term rental",
      status: "COMPLIANT_UNTIL_RENEWAL_DATE" as const,
      renewal_date: null,
      color: strPropertyMapPinColor(pin),
      strCard: {
        statusLabel: pin.status_label,
        platforms: pin.platforms ?? [],
        heroImageUrl: safeHttpUrl(pin.hero_image_url),
        addressUnverified: pin.address_unverified === true,
      },
    }));
}

function columnProperties(
  key: ColumnKey,
): ColumnProperties<StrGovernmentProperty, ColumnKey> {
  switch (key) {
    case "property":
      return {
        displayName: "Property",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => row.address || row.title || "Short-term rental",
      };
    case "status":
      return {
        displayName: "Status",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => strStatusDisplayLabel(row.status_label),
      };
    case "regions":
      return {
        displayName: "Regions",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => row.regions.join(", ") || "Unassigned",
      };
    case "platforms":
      return {
        displayName: "Platforms",
        defaultVisible: true,
        getValue: (row) => row.platforms.join(", ") || "(Empty)",
      };
    case "city":
      return {
        displayName: "City",
        defaultVisible: true,
        getValue: (row) => row.city || "(Empty)",
      };
    case "listings":
      return {
        displayName: "Listings",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => String(row.listing_count),
      };
  }
}

export function GovernmentStrPage({
  search,
  onSearchChange,
}: {
  search?: StrCatalogSearch;
  onSearchChange?: (search: StrCatalogSearch) => void;
} = {}) {
  const [localSearch, setLocalSearch] = useState(() =>
    validateGovernmentStrSearch({}),
  );
  const catalogSearch = search ?? localSearch;
  const changeSearch = onSearchChange ?? setLocalSearch;
  const dashboardState = useGovernmentUserDashboardState();
  const regionParams = regionQueryParams(dashboardState.selectedRegions);
  const filters = useMemo(
    () => parseStrCatalogFilters(catalogSearch.filters),
    [catalogSearch.filters],
  );
  const [facetValues, setFacetValues] = useState<
    Partial<Record<ColumnKey, string[]>>
  >({});
  const filterRequestIds = useRef<Partial<Record<ColumnKey, number>>>({});
  const [selected, setSelected] = useState<StrGovernmentProperty | null>(null);
  const [licenseId, setLicenseId] = useState<string | null>(null);
  const listQuery = useQuery({
    queryKey: ["government-str", catalogSearch, dashboardState.selectedRegions],
    queryFn: () =>
      getGovernmentStrProperties({
        page: catalogSearch.page,
        pageSize: catalogSearch.pageSize,
        regionIds: regionParams.region_ids,
        includeUnassignedRegions: regionParams.include_unassigned_regions,
        sort: catalogSearch.sort,
        direction: catalogSearch.direction,
        filters: catalogSearch.filters || undefined,
      }),
  });
  const pinsQuery = useQuery({
    queryKey: [
      "government-str-pins",
      catalogSearch.filters,
      dashboardState.selectedRegions,
    ],
    queryFn: () =>
      getGovernmentStrPins({
        regionIds: regionParams.region_ids,
        includeUnassignedRegions: regionParams.include_unassigned_regions,
        filters: catalogSearch.filters || undefined,
      }),
    enabled:
      listQuery.data?.configured === true && catalogSearch.view === "map",
  });
  const activeColumnKeys = useMemo(
    () =>
      listQuery.data?.show_regions === false
        ? COLUMN_KEYS.filter((key) => key !== "regions")
        : COLUMN_KEYS,
    [listQuery.data?.show_regions],
  );
  const detailQuery = useQuery({
    queryKey: ["government-str-detail", selected?.id],
    queryFn: () => getGovernmentStrProperty(selected?.id ?? ""),
    enabled: selected != null,
  });
  const jurisdictionId = listQuery.data?.jurisdiction_id ?? null;
  const regionFilter = useRegionFilterOptions({
    jurisdictionIds: jurisdictionId ? [jurisdictionId] : [],
    jurisdictionNames:
      jurisdictionId && listQuery.data?.jurisdiction_name
        ? { [jurisdictionId]: listQuery.data.jurisdiction_name }
        : {},
    enabled: jurisdictionId != null,
  });

  const openProperty = (row: StrGovernmentProperty) => {
    setLicenseId(null);
    setSelected(row);
  };
  const governmentColumns = useCallback(
    (key: ColumnKey) => ({
      ...columnProperties(key),
      ...(facetValues[key] ? { filterValues: facetValues[key] } : {}),
    }),
    [facetValues],
  );
  const columnFilterBadges = useMemo(
    () => buildColumnFilterBadges(filters, activeColumnKeys, columnProperties),
    [activeColumnKeys, filters],
  );
  const filtersRef = useRef(filters);
  const seenUrlFilters = useRef(catalogSearch.filters);
  if (seenUrlFilters.current !== catalogSearch.filters) {
    seenUrlFilters.current = catalogSearch.filters;
    filtersRef.current = filters;
  }
  const searchRef = useRef(catalogSearch);
  searchRef.current = catalogSearch;
  const setSharedCatalogFilters = useCallback(
    (action: SetStateAction<Record<string, string[]>>) => {
      const current = filtersRef.current;
      const next = typeof action === "function" ? action(current) : action;
      const serialized = serializeStrCatalogFilters(next);
      filtersRef.current = next;
      if (serialized === searchRef.current.filters) return;
      seenUrlFilters.current = serialized;
      changeSearch({ ...searchRef.current, page: 1, filters: serialized });
    },
    [changeSearch],
  );
  const licenseColumnFilters = dashboardState.columnFilters ?? {};
  const sharedTextBuckets = useMemo(
    () =>
      dashboardState.setColumnFilters
        ? [
            {
              filters: licenseColumnFilters,
              setFilters: dashboardState.setColumnFilters,
              columns: LICENSE_SHARED_TEXT_COLUMNS,
            },
            {
              filters,
              setFilters: setSharedCatalogFilters,
              columns: STR_SHARED_TEXT_COLUMNS,
            },
          ]
        : [],
    [
      dashboardState.setColumnFilters,
      filters,
      licenseColumnFilters,
      setSharedCatalogFilters,
    ],
  );
  useSyncSharedTextColumns({ buckets: sharedTextBuckets });
  const handleFiltersChange = setSharedCatalogFilters;
  useListColumnFilterSync({
    columnFilters: filters,
    setColumnFilters: setSharedCatalogFilters,
    regionColumnId: "regions",
    selectedRegions: dashboardState.selectedRegions,
    setSelectedRegions: dashboardState.setSelectedRegions,
    regions: regionFilter.catalog,
  });
  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      const current: SortingState = [
        {
          id: catalogSearch.sort,
          desc: catalogSearch.direction === "desc",
        },
      ];
      const next = typeof updater === "function" ? updater(current) : updater;
      changeSearch({
        ...catalogSearch,
        page: 1,
        sort: next[0]?.id ?? "property",
        direction: next[0]?.desc ? "desc" : "asc",
      });
    },
    [catalogSearch, changeSearch],
  );
  const handleFilterPopoverOpen = useCallback(
    async (columnId: string, q?: string) => {
      if (!activeColumnKeys.includes(columnId as ColumnKey)) return;
      const key = columnId as ColumnKey;
      const requestId = (filterRequestIds.current[key] ?? 0) + 1;
      filterRequestIds.current[key] = requestId;
      const values = await getGovernmentStrPropertyFilterValues({
        column: key,
        q,
        filters: catalogSearch.filters || undefined,
        regionIds: regionParams.region_ids,
        includeUnassignedRegions: regionParams.include_unassigned_regions,
      });
      if (filterRequestIds.current[key] === requestId) {
        setFacetValues((current) => ({ ...current, [key]: values }));
      }
    },
    [
      catalogSearch.filters,
      activeColumnKeys,
      regionParams.include_unassigned_regions,
      regionParams.region_ids,
    ],
  );

  if (listQuery.isLoading) {
    return <LoadingScreen message="Fetching short-term rentals..." />;
  }
  if (listQuery.error) {
    return (
      <ErrorComponent
        message={getQueryErrorMessage(listQuery.error)}
        action={{ label: "Retry", onClick: () => void listQuery.refetch() }}
      />
    );
  }
  const pageData = listQuery.data;
  if (!pageData) return null;
  if (!pageData.configured && pageData.contact) {
    const contact = pageData.contact;
    return (
      <section className="mx-auto max-w-xl space-y-3 p-6">
        <h1 className="text-2xl font-semibold">Short-Term Rentals</h1>
        <p>{contact.heading}</p>
        <p className="font-medium">{contact.name}</p>
        <p>{contact.title}</p>
        <p>
          <a href={`tel:${contact.phone.replaceAll(".", "")}`}>
            {contact.phone}
          </a>
        </p>
        <p>
          <a href={`mailto:${contact.email}`}>{contact.email}</a>
        </p>
        <p>
          <a href={contact.website} target="_blank" rel="noopener noreferrer">
            {contact.website}
          </a>
        </p>
        <p>
          <a
            href={contact.booking_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Book a meeting
          </a>
        </p>
      </section>
    );
  }

  const pins = governmentStrMapPins(pinsQuery.data ?? []);
  const chrome = (
    <div className="space-y-4 pb-2">
      <div>
        <h1 className="text-2xl font-semibold">Short-term rentals</h1>
        <p className="text-sm text-muted-foreground">
          One row for each property in this jurisdiction. Listings marked as the
          same, two units at the same address, or a portion of each other share
          a row.
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ul className="flex flex-wrap gap-3 text-sm" aria-label="Map legend">
          {STR_PROPERTY_PIN_LEGEND.map((item) => (
            <li key={item.label} className="flex items-center gap-1">
              <span
                className="inline-block h-3 w-3 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              {item.label}
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap items-center gap-2">
          <DashboardViewToggle
            isMapView={catalogSearch.view === "map"}
            onViewChange={(isMapView) => {
              dashboardState.handleMapViewChange(isMapView);
              changeSearch({
                ...catalogSearch,
                view: isMapView ? "map" : "list",
              });
            }}
          />
        </div>
      </div>
    </div>
  );

  const details = (
    <>
      <StrPropertyDetailsSheet
        open={selected != null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
        title={selected?.address || selected?.title || "Property"}
        loading={
          selected != null && detailQuery.data == null && !detailQuery.isError
        }
        errorMessage={
          detailQuery.isError ? getQueryErrorMessage(detailQuery.error) : null
        }
        detail={
          detailQuery.data
            ? {
                address: detailQuery.data.group.address,
                addressUnverified: detailQuery.data.group.address_unverified,
                regions: detailQuery.data.group.regions,
                latitude: detailQuery.data.group.latitude,
                longitude: detailQuery.data.group.longitude,
                listings: detailQuery.data.listings,
              }
            : null
        }
        licenses={detailQuery.data?.group.licenses ?? selected?.licenses ?? []}
        onOpenLicense={(openedLicenseId) => {
          const licenses =
            detailQuery.data?.group.licenses ?? selected?.licenses ?? [];
          const scoped = scopedStrLicenseId(openedLicenseId, licenses);
          if (scoped) setLicenseId(scoped);
        }}
      />
      <StrLinkedLicenseSheet
        licenseId={licenseId}
        onLicenseIdChange={setLicenseId}
        userType={UserType.GOVERNMENT}
      />
    </>
  );

  const handleExport = async (
    mode: ExportColumnMode,
    format: "csv" | "excel",
  ) => {
    const rows = await getAllGovernmentStrProperties({
      regionIds: regionParams.region_ids,
      includeUnassignedRegions: regionParams.include_unassigned_regions,
      sort: catalogSearch.sort,
      direction: catalogSearch.direction,
      filters: catalogSearch.filters || undefined,
    });
    const layoutKey = dashboardTableLayoutBaseKey("government-str-properties");
    const visibility = JSON.parse(
      localStorage.getItem(`${layoutKey}:visibility`) ?? "{}",
    ) as Record<ColumnKey, boolean>;
    const exportKeys = getExportAccessorKeysForMode(
      mode,
      null,
      activeColumnKeys,
      undefined,
      visibility,
    );
    const input = {
      rows,
      exportKeys: exportKeys as ColumnKey[],
      getColumnProperties: columnProperties,
      fileBasename: "government-str-properties",
    };
    if (format === "csv") exportTableRowsToCsv(input);
    else exportTableRowsToExcel(input);
  };
  const frame = (
    <StrCatalogFrame<StrGovernmentProperty, ColumnKey>
      chrome={
        <>
          {chrome}
          {pageData.items.length === 0 && catalogSearch.view === "list" ? (
            <p>No short-term rentals in this jurisdiction.</p>
          ) : null}
        </>
      }
      isMapView={catalogSearch.view === "map"}
      onListView={() => changeSearch({ ...catalogSearch, view: "list" })}
      pins={pins}
      infoCard="str-property"
      pinsLoading={pinsQuery.isFetching}
      pinsError={pinsQuery.error}
      onPinsRetry={() => void pinsQuery.refetch()}
      onPinClick={(id) => {
        const row = pageData.items.find((item) => item.id === id);
        const pin = (pinsQuery.data ?? []).find((item) => item.id === id);
        if (row) openProperty(row);
        else if (pin) openProperty(propertyFromPin(pin));
      }}
      regionOverlay={
        jurisdictionId
          ? {
              selectedJurisdictionIds: [jurisdictionId],
              portalJurisdictionIds: [jurisdictionId],
              onRegionFilterChange: dashboardState.setSelectedRegions,
            }
          : undefined
      }
      columnFilterBadges={columnFilterBadges}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      accessorKeys={activeColumnKeys}
      getColumnProperties={governmentColumns}
      tablePersistenceKey="government-str-properties"
      rows={pageData.items}
      getRowId={(row) => row.id}
      onRowClick={(id) => {
        const row = pageData.items.find((item) => item.id === id);
        if (row) openProperty(row);
      }}
      columnFilters={filters}
      onColumnFiltersChange={handleFiltersChange}
      serverMode
      lockToViewport
      rowCount={pageData.total}
      pageIndex={catalogSearch.page - 1}
      pageSize={catalogSearch.pageSize}
      pageSizeOptions={PAGE_SIZES}
      onPaginationChange={(next) => {
        const nextSize = (PAGE_SIZES as readonly number[]).includes(
          next.pageSize,
        )
          ? (next.pageSize as PageSize)
          : catalogSearch.pageSize;
        changeSearch({
          ...catalogSearch,
          page: nextSize === catalogSearch.pageSize ? next.pageIndex + 1 : 1,
          pageSize: nextSize,
        });
      }}
      sorting={[
        {
          id: catalogSearch.sort,
          desc: catalogSearch.direction === "desc",
        },
      ]}
      onSortingChange={handleSortingChange}
      onFilterPopoverOpen={handleFilterPopoverOpen}
      onServerExport={handleExport}
      onRefresh={() => void listQuery.refetch()}
      isRefreshing={listQuery.isFetching}
      refreshAriaLabel="Refresh short-term rentals"
      dataUpdatedAt={listQuery.dataUpdatedAt}
    />
  );
  return (
    <>
      {frame}
      {details}
    </>
  );
}
